import { z } from 'zod'
import { supabase } from './supabase'
import type { Account, Category, DashboardSummary, PortfolioSummary, TransactionDraft, TransactionRow } from '../types'

const dashboardSchema = z.object({
  total_balance_idr: z.string(),
  savings_balance_idr: z.string(),
  asset_value_idr: z.string(),
  income_idr: z.string(),
  expense_idr: z.string(),
  salary_unpaid_idr: z.string(),
  freelance_uncollected_idr: z.string(),
  accounts: z.array(z.object({
    id: z.string(), name: z.string(), kind: z.enum(['cash', 'bank', 'ewallet', 'investment']),
    purpose: z.enum(['operational', 'savings', 'investment']), balance_idr: z.string(), is_default: z.boolean(), archived_at: z.string().nullable(),
  })),
  recent_transactions: z.array(z.object({
    id: z.string(), event_type: z.string(), local_date: z.string(), description: z.string(),
    amount_idr: z.string(), account_name: z.string().nullable(), category_name: z.string().nullable(),
    status: z.enum(['posted', 'reversed']), created_at: z.string(),
  })),
})

function client() {
  if (!supabase) throw new Error('Supabase belum dikonfigurasi.')
  return supabase
}

export async function getDashboardSummary(): Promise<DashboardSummary> {
  const { data, error } = await client().rpc('get_dashboard_summary')
  if (error) throw error
  return dashboardSchema.parse(data)
}

export async function getTransactions(options: {
  from?: string
  to?: string
  cursor?: string
  limit?: number
} = {}): Promise<TransactionRow[]> {
  const { data, error } = await client().rpc('list_transactions', {
    p_from: options.from ?? null,
    p_to: options.to ?? null,
    p_cursor: options.cursor ?? null,
    p_limit: options.limit ?? 30,
  })
  if (error) throw error
  return z.array(dashboardSchema.shape.recent_transactions.element).parse(data)
}

export async function getInputContext(): Promise<{ accounts: Account[]; categories: Category[]; defaultAccountId: string | null }> {
  const db = client()
  const [accountsResult, categoriesResult, settingsResult] = await Promise.all([
    db.from('account_balances').select('id,name,kind,purpose,balance_idr,is_default,archived_at').is('archived_at', null).neq('purpose', 'investment').order('name'),
    db.from('categories').select('id,name,kind,aliases,parent_id,archived_at').is('archived_at', null).order('name'),
    db.from('settings').select('default_account_id').single(),
  ])
  if (accountsResult.error) throw accountsResult.error
  if (categoriesResult.error) throw categoriesResult.error
  if (settingsResult.error && settingsResult.error.code !== 'PGRST116') throw settingsResult.error
  return {
    accounts: z.array(dashboardSchema.shape.accounts.element).parse(accountsResult.data),
    categories: z.array(z.object({
      id: z.string(), name: z.string(), kind: z.enum(['income', 'expense']), aliases: z.array(z.string()),
      parent_id: z.string().nullable(), archived_at: z.string().nullable(),
    })).parse(categoriesResult.data),
    defaultAccountId: settingsResult.data?.default_account_id ?? null,
  }
}

const portfolioSchema = z.object({
  savings_balance_idr: z.string(), asset_cost_idr: z.string(), asset_value_idr: z.string(),
  savings_accounts: z.array(dashboardSchema.shape.accounts.element),
  assets: z.array(z.object({
    id: z.string(), kind: z.enum(['gold', 'crypto', 'other']), name: z.string(), symbol: z.string(), unit: z.string(),
    quantity: z.string(), total_cost_idr: z.string(), current_price_idr: z.string(), current_value_idr: z.string(),
    linked_account_id: z.string(), updated_at: z.string(),
  })),
})

export async function getPortfolioSummary(): Promise<PortfolioSummary> {
  const { data, error } = await client().rpc('get_portfolio_summary')
  if (error) throw error
  return portfolioSchema.parse(data)
}

export async function createFinancialAsset(input: { kind: 'gold' | 'crypto' | 'other'; name: string; symbol: string; unit: string }): Promise<string> {
  const { data, error } = await client().rpc('create_financial_asset', {
    p_kind: input.kind, p_name: input.name, p_symbol: input.symbol, p_unit: input.unit,
  })
  if (error) throw error
  return z.string().parse(data)
}

export async function recordAssetPurchase(input: {
  assetId: string; sourceAccountId: string; quantity: string; amountIdr: string; feeIdr: string; localDate: string; idempotencyKey: string
}): Promise<{ id: string; replayed: boolean }> {
  const { data, error } = await client().rpc('record_asset_purchase', {
    p_asset_id: input.assetId,
    p_source_account_id: input.sourceAccountId,
    p_quantity: input.quantity,
    p_amount_idr: input.amountIdr,
    p_fee_idr: input.feeIdr,
    p_local_date: input.localDate,
    p_idempotency_key: input.idempotencyKey,
  })
  if (error) throw error
  return z.object({ id: z.string(), replayed: z.boolean() }).parse(data)
}

export async function updateFinancialAssetPrice(assetId: string, currentPriceIdr: string): Promise<void> {
  const { error } = await client().rpc('update_financial_asset_price', { p_asset_id: assetId, p_current_price_idr: currentPriceIdr })
  if (error) throw error
}

export async function postTransaction(draft: TransactionDraft, idempotencyKey: string, negativeConsent?: {
  accountId: string
  balanceSnapshot: string
}): Promise<{ id: string; replayed: boolean }> {
  const { data, error } = await client().rpc('post_financial_transaction', {
    p_payload: {
      kind: draft.kind,
      amount_idr: draft.amount_idr,
      local_date: draft.local_date,
      description: draft.description,
      category_id: draft.category_id,
      source_account_id: draft.source_account_id,
      destination_account_id: draft.destination_account_id,
      fee_idr: draft.fee_idr,
    },
    p_idempotency_key: idempotencyKey,
    p_negative_consent: negativeConsent ?? null,
  })
  if (error) throw error
  return z.object({ id: z.string(), replayed: z.boolean() }).parse(data)
}

export async function previewTransaction(draft: TransactionDraft): Promise<{
  would_be_negative: boolean
  account_id: string | null
  balance_snapshot_idr: string | null
  projected_balance_idr: string | null
  budget_warning: string | null
}> {
  const { data, error } = await client().rpc('preview_financial_transaction', {
    p_payload: {
      kind: draft.kind,
      amount_idr: draft.amount_idr,
      local_date: draft.local_date,
      category_id: draft.category_id,
      source_account_id: draft.source_account_id,
      destination_account_id: draft.destination_account_id,
      fee_idr: draft.fee_idr,
    },
  })
  if (error) throw error
  return z.object({
    would_be_negative: z.boolean(), account_id: z.string().nullable(),
    balance_snapshot_idr: z.string().nullable(), projected_balance_idr: z.string().nullable(),
    budget_warning: z.string().nullable(),
  }).parse(data)
}

export async function reverseTransaction(transactionId: string, reason: string, idempotencyKey: string): Promise<string> {
  const { data, error } = await client().rpc('reverse_financial_transaction', {
    p_transaction_id: transactionId,
    p_reason: reason,
    p_idempotency_key: idempotencyKey,
  })
  if (error) throw error
  return z.string().parse(data)
}
